import React, { createContext, useContext, useLayoutEffect, useEffect, useMemo } from 'react';
import { ArenaStoreApi, createArenaStore } from './createArenaStore';

export const ArenaStoreContext = createContext<ArenaStoreApi | null>(null);

/**
 * Global registry mapping tableId -> active table store instance.
 * Allows non-React background services (e.g. ArenaSubscriptionService) to access the store for a specific table.
 */
const tableStoreRegistry = new Map<string, ArenaStoreApi>();
let latestActiveStore: ArenaStoreApi | null = null;

export function getTableStore(tableId?: string | null): ArenaStoreApi | null {
  if (tableId && tableStoreRegistry.has(tableId)) {
    return tableStoreRegistry.get(tableId)!;
  }
  if (latestActiveStore) {
    return latestActiveStore;
  }
  if (tableStoreRegistry.size > 0) {
    return Array.from(tableStoreRegistry.values())[0];
  }
  return null;
}

export function registerTableStore(tableId: string | null, store: ArenaStoreApi) {
  if (tableId) {
    tableStoreRegistry.set(tableId, store);
  }
  latestActiveStore = store;
}

export function unregisterTableStore(tableId: string | null, store: ArenaStoreApi) {
  if (tableId && tableStoreRegistry.get(tableId) === store) {
    tableStoreRegistry.delete(tableId);
  }
  if (latestActiveStore === store) {
    latestActiveStore = tableStoreRegistry.size > 0 ? Array.from(tableStoreRegistry.values())[0] : null;
  }
}

export interface ArenaStoreProviderProps {
  tableId?: string | null;
  store?: ArenaStoreApi;
  children: React.ReactNode;
}

/**
 * Store-per-table Provider.
 * Instantiates an isolated Zustand store for this table session and binds it to React Context.
 */
export const ArenaStoreProvider: React.FC<ArenaStoreProviderProps> = ({
  tableId,
  store: externalStore,
  children,
}) => {
  const store = useMemo(() => {
    const s = externalStore ?? createArenaStore(tableId);
    // Immediately register store synchronously so child hooks/effects and synchronous RTDB updates find it on mount
    registerTableStore(tableId ?? null, s);
    return s;
  }, [tableId, externalStore]);

  useLayoutEffect(() => {
    registerTableStore(tableId ?? null, store);
  }, [tableId, store]);

  useEffect(() => {
    registerTableStore(tableId ?? null, store);
    if (typeof window !== 'undefined' && (window as any).__E2E_MOCK_USER__) {
      (window as any).__ARENA_STORE__ = store;
    }
    return () => {
      unregisterTableStore(tableId ?? null, store);
      if (typeof window !== 'undefined' && (window as any).__E2E_MOCK_USER__) {
        (window as any).__ARENA_STORE__ = store;
      }
    };
  }, [tableId, store]);

  return <ArenaStoreContext.Provider value={store}>{children}</ArenaStoreContext.Provider>;
};

export const useArenaStoreApi = (): ArenaStoreApi | null => {
  return useContext(ArenaStoreContext);
};
